/**
 * Types du modèle de données (brief §3).
 *
 * Source de vérité unique : ces types décrivent exactement les lignes des tables
 * créées par server/src/db/migrations/001_initial.sql. Toute modification du
 * schema SQL doit être répercutée ici dans la même migration.
 *
 * Conventions de correspondance SQLite -> TypeScript :
 *   - colonne nullable      -> `| null` (jamais `?`, jamais `undefined`)
 *   - INTEGER 0 / 1         -> `Bool01`
 *   - TEXT date ISO 8601    -> `Iso8601`
 */

/** Horodatage ISO 8601 en UTC, ex. "2026-08-02T14:30:00.000Z". */
export type Iso8601 = string;

/** SQLite n'a pas de booléen : 0 = faux, 1 = vrai. */
export type Bool01 = 0 | 1;

/** UUID v4. */
export type Id = string;

// --------------------------------------------------------------------------
// Énumérations
// --------------------------------------------------------------------------

/** Comportement de mise à l'échelle d'un ingredient (§4.1). */
export const SCALING_MODES = ['linear', 'none', 'sublinear'] as const;
export type Scaling = (typeof SCALING_MODES)[number];

/** Les tags `auto` sont recalculés, jamais saisis (§3). */
export const TAG_KINDS = ['manual', 'auto'] as const;
export type TagKind = (typeof TAG_KINDS)[number];

/**
 * Statut de rangement d'une recette (US-21, migration
 * `002_recipe_status.sql`). Porte une **intention** de l'utilisateur, et elle
 * seule : « à refaire » et « jamais cuisinée » se déduisent du journal de
 * réalisations et ne sont pas stockées ici. `null` — aucune intention
 * déclarée — est le cas de l'immense majorité des recettes.
 */
export const RECIPE_STATUSES = ['a_cuisiner', 'favorite'] as const;
export type RecipeStatus = (typeof RECIPE_STATUSES)[number];

/** Vérifie une valeur venue de l'extérieur (paramètre d'URL, corps de requête) avant de la traiter comme un statut. */
export function isRecipeStatus(value: unknown): value is RecipeStatus {
  return typeof value === 'string' && (RECIPE_STATUSES as readonly string[]).includes(value);
}

/** Difficulté : 1 a 3, nullable. */
export type Difficulty = 1 | 2 | 3;

/** Note d'une réalisation : 1 a 5. */
export type Rating = 1 | 2 | 3 | 4 | 5;

/**
 * Unités canoniques stockées en base (§4.2). Toute autre ecriture est
 * convertie à l'entrée par le parseur d'ingredients.
 * Les facteurs de conversion ne sont PAS ici : ils appartiennent au module de
 * parsing / conversion (§4.2), implémenté avec US-01 et US-07.
 */
export const CANONICAL_UNITS = [
  // masse
  'g',
  'kg',
  // volume
  'ml',
  'cl',
  'l',
  'c. à c.',
  'c. à s.',
  // dénombré
  'piece',
  'pincee',
  'botte',
  'gousse',
  'brin',
  'tranche',
  // domestique (US-26) : mesures d'une recette transmise par message, sans
  // équivalent métrique fixe — le pot est celui du yaourt, le verre celui du
  // buffet. Canoniques plutôt que converties : convertir demanderait de
  // deviner une quantité, ce que le §3.2 interdit.
  'pot',
  'sachet',
  'verre',
  'cuillere',
  'bol',
  'noix',
  // anglo
  'cup',
  'oz',
  'lb',
  'tsp',
  'tbsp',
] as const;
export type Unit = (typeof CANONICAL_UNITS)[number];

// --------------------------------------------------------------------------
// Tables
// --------------------------------------------------------------------------

/** Table `recipe`. On archive, on ne supprime jamais. */
export interface Recipe {
  id: Id;
  title: string;
  description: string | null;
  /** URL d'origine si import web. */
  source_url: string | null;
  /**
   * D'où vient la recette : le site, le livre. Champ libre.
   *
   * Depuis US-27, **ne contient plus la personne** : elle a sa propre colonne
   * (`author`). Les mélanger faisait afficher un pseudonyme dans un champ
   * intitulé « Source », le site n'apparaissant alors nulle part.
   */
  source_name: string | null;
  /** Qui a écrit la recette (US-27, migration `004_recipe_author.sql`). Nullable : une recette de famille n'a pas d'auteur déclaré. */
  author: string | null;
  /** Chemin relatif de la photo principale, dans PHOTOS_DIR. */
  image_path: string | null;
  /** Portions de la recette de référence. Dénominateur du facteur d'adaptation (§4.1). */
  servings: number | null;
  /** « personnes », « parts », « pièces »… */
  servings_label: string | null;
  /** Temps de préparation actif, en minutes. */
  prep_min: number | null;
  /** Temps de repos / passif, en minutes. */
  rest_min: number | null;
  /** Temps de cuisson, en minutes. */
  cook_min: number | null;
  difficulty: Difficulty | null;
  /** Notes personnelles permanentes, distinctes du journal. */
  notes: string | null;
  archived: Bool01;
  /**
   * Ce que la recette **est** (US-55) : une entrée, un plat, un dessert…
   * Une seule valeur, et elle décrit la recette — donc **commune**, au même
   * titre que le titre ou le temps de cuisson, contrairement au rangement qui
   * dépend de qui regarde (§1).
   *
   * `null` tant que l'information manque : on ne devine jamais (§1). Un import
   * dont la page n'annonce pas de catégorie laisse la colonne vide plutôt que
   * de choisir à la place de quelqu'un.
   */
  course: RecipeCourse | null;
  /**
   * D'où vient la recette : « italienne », « alsacienne », « antillaise ».
   * Champ libre — le monde est plus grand qu'une liste fermée — avec
   * autocomplétion sur les valeurs déjà saisies **du périmètre visible**.
   */
  cuisine: string | null;
  /** Forme de regroupement, jamais affichée. Voir `server/src/recipes/normalizeCuisine.ts`. */
  cuisine_normalized: string | null;
  /**
   * Déjà cuisinée avant son arrivée dans l'application, à une date inconnue
   * (migration `008`). Ni une réalisation, ni une absence de réalisation :
   * l'aveu que l'information manque. Retire la seule étiquette « jamais
   * cuisinée » (US-05), sans rien ajouter au journal.
   */
  cooked_before: Bool01;
  /**
   * Intention de rangement déclarée par l'utilisateur (US-21). `null` tant
   * qu'il n'a rien déclaré — ce n'est pas une valeur par défaut à afficher.
   */
  status: RecipeStatus | null;
  /**
   * Propriétaire (US-43). `null` = ligne d'avant la migration `010` pas encore
   * adoptée ; l'adoption a lieu à la création du premier compte.
   */
  owner_id: Id | null;
  /** `private` par défaut : un oubli ne publie rien (US-43). */
  visibility: RecipeVisibility;
  /** Dernier membre à avoir écrit. Porte « modifiée par Claire » (US-49). */
  updated_by: Id | null;
  /** Recette dont celle-ci est une copie (US-49). Porte « d'après le pot-au-feu d'Arthur ». */
  forked_from: Id | null;
  created_at: Iso8601;
  updated_at: Iso8601;
}

/** Table `ingredient`. */
export interface Ingredient {
  id: Id;
  recipe_id: Id;
  position: number;
  /** « Pour la pâte », « Pour la garniture ». */
  group_label: string | null;
  /** Nullable : « sel, poivre » n'a pas de quantité. Jamais devinée (§4.2). */
  quantity: number | null;
  unit: Unit | null;
  /** Nom affiché, tel qu'écrit dans la recette. Contient la ligne source entière si non parsable. */
  name: string;
  /** Minuscules, sans accent, au singulier. Recherche et regroupement. */
  name_normalized: string | null;
  /** « finement émincé », « à température ambiante ». */
  note: string | null;
  is_optional: Bool01;
  scaling: Scaling;
}

/**
 * Origine d'une valeur détectée (US-78, migration `028`) : `'auto'` — écrite
 * par la détection à l'enregistrement —, `'manuel'` — touchée à la main, y
 * compris vidée, et la détection ne la reprend plus jamais —, `null` — rien
 * n'a encore été écrit.
 */
export type OrigineDetection = 'auto' | 'manuel';

/** Table `step`. */
export interface Step {
  id: Id;
  recipe_id: Id;
  position: number;
  /** Phase. */
  group_label: string | null;
  instruction: string;
  /**
   * **Vidée par la migration `030` et plus jamais lue** (US-98), comme
   * `recipe.status` depuis `013` et `recipe.notes` depuis `019`. Sa valeur est
   * passée dans `duration_sec`, en secondes ; la colonne subsiste — une
   * migration appliquée ne se modifie pas — et une requête qui l'interroge lit
   * `null`.
   *
   * L'avoir **vidée** plutôt que laissée en place est ce qui rend la bascule
   * sûre : un lecteur oublié quelque part n'affiche plus de minuteur, au lieu
   * de lire des minutes là où il y a des secondes. Le premier défaut se voit
   * tout de suite ; le second fait brûler un plat.
   */
  duration_min: number | null;
  /**
   * Durée de l'étape, **en secondes** (US-98), détectée à l'enregistrement
   * (US-78) ou saisie. Alimente le bouton minuteur (§4.3).
   *
   * La seconde est l'unité parce que c'est celle des recettes d'appareil :
   * « Mixer 20 sec / vitesse 5 » est la phrase la plus fréquente du fonds
   * Thermomix, et 329 étapes n'avaient aucun minuteur faute de pouvoir la
   * stocker. Les temps de la **recette** (`prep_min`, `rest_min`, `cook_min`)
   * restent en minutes : personne n'écrit une préparation en secondes.
   */
  duration_sec: number | null;
  temperature_c: number | null;
  duration_source: OrigineDetection | null;
  temperature_source: OrigineDetection | null;
  /** 1 si l'étape n'occupe pas le cuisinier. Nécessaire à la chronologie V3. */
  is_passive: Bool01;
}

/** Table `cook_log` : le journal de réalisation, cœur de la différenciation (§1.4). */
export interface CookLog {
  id: Id;
  recipe_id: Id;
  /** Date de réalisation, modifiable a posteriori. */
  cooked_at: Iso8601;
  servings_made: number | null;
  rating: Rating | null;
  /** Temps réel constaté, en minutes. */
  actual_minutes: number | null;
  what_happened: string | null;
  /** Réaffiché au prochain lancement du mode cuisine (US-11). */
  next_time: string | null;
  photo_path: string | null;
  /**
   * Auteur de la réalisation (US-43). Le journal est personnel : c'est le bon
   * défaut, et le plus attendu — `what_happened` et `next_time` sont les
   * ratés, et les rendre publics découragerait de les écrire.
   */
  member_id: Id | null;
  /**
   * Réalisation partagée avec la famille (US-65). `0` par défaut, et **toutes
   * les réalisations d'avant la migration `020` le restent** : personne n'a
   * écrit ses ratages en sachant qu'ils seraient lus.
   *
   * Trois règles inséparables :
   *
   * - une réalisation ne peut être partagée que si **sa recette l'est** ;
   * - une nouvelle réalisation sur une recette partagée l'est par défaut, avec
   *   une bascule pour la garder pour soi ;
   * - **`next_time` n'est jamais partagé**, quel que soit cet indicateur.
   */
  shared: Bool01;
  /**
   * Prénom de l'auteur, calculé (US-65) — jamais une colonne. `null` pour les
   * réalisations d'avant la migration `010`, qui n'ont pas d'auteur.
   *
   * Le même calcul que partout ailleurs (`displayNameResolver`) : le prénom
   * seul, ou « Claire M. » si deux membres actifs le partagent.
   */
  member_name?: string | null;
  /**
   * Commentaires et réactions (US-66), calculés pour le demandeur — jamais des
   * colonnes. Absents des réponses qui n'ont pas à les porter (la liste de la
   * bibliothèque, par exemple).
   */
  social?: CookLogSocial;
}

/**
 * Les quatre réactions possibles (US-66). Liste **fermée**, rendues en SVG :
 * un compteur de « j'aime » multiples et des emoji libres ont tous deux été
 * écartés — le premier compte des clics, les seconds changent de dessin d'un
 * système à l'autre.
 */
export const REACTION_KINDS = ['bravo', 'envie', 'question', 'refaire'] as const;
export type ReactionKind = (typeof REACTION_KINDS)[number];

export function isReactionKind(value: unknown): value is ReactionKind {
  return typeof value === 'string' && (REACTION_KINDS as readonly string[]).includes(value);
}

/** Un commentaire sur une réalisation (US-66). Supprimé, il laisse une trace neutre plutôt qu'un trou. */
export interface CookLogComment {
  id: Id;
  cook_log_id: Id;
  member_id: Id;
  /** `null` quand le commentaire a été supprimé : la ligne reste, le texte non. */
  body: string | null;
  member_name: string | null;
  created_at: Iso8601;
  deleted_at: Iso8601 | null;
  /** L'auteur, ou le propriétaire de la recette, peut le supprimer. Calculé par le serveur pour ce demandeur. */
  can_delete: boolean;
}

/** Ce qui s'attache à une réalisation, calculé pour le demandeur (US-66). */
export interface CookLogSocial {
  comments: CookLogComment[];
  /** Nombre de réactions par nature, dans le périmètre visible. Les natures à zéro ne sont pas renvoyées. */
  reactions: Partial<Record<ReactionKind, number>>;
  /** La réaction du demandeur, ou `null`. */
  my_reaction: ReactionKind | null;
}

/** Table `tag`. */
export interface Tag {
  id: Id;
  name: string;
  kind: TagKind;
}

/** Table `recipe_tag`. Clé primaire composite. */
export interface RecipeTag {
  recipe_id: Id;
  tag_id: Id;
}

/**
 * Table `collection` (US-21) : une **recherche sauvegardée**, pas un dossier
 * de recettes. Voir `003_collection.sql` — un dossier se périme dès qu'une
 * nouvelle recette arrive, une recherche non.
 */
export interface Collection {
  id: Id;
  name: string;
  /** `CollectionQuery` sérialisé (shared/src/api.ts). Jamais interrogé en SQL. */
  query_json: string;
  /** Ordre d'affichage dans la barre de filtres, choisi par l'utilisateur. */
  position: number;
  /** Propriétaire de ce rangement (US-43). Une collection est personnelle. */
  member_id: Id | null;
}

/**
 * Créneaux d'un plan de repas (US-36). Deux par jour : c'est ce qui se
 * planifie réellement à la maison. Le petit-déjeuner ne se décide pas la
 * veille, et l'ajouter ferait une grille au tiers vide.
 */
export const MEAL_SLOTS = ['midi', 'soir'] as const;
export type MealSlot = (typeof MEAL_SLOTS)[number];

/**
 * Table `meal_plan` (US-36) : un repas planifié. `date` est un jour seul
 * (« 2026-08-12 ») et non un horodatage — un plan de semaine se raisonne en
 * jours, et une heure obligerait à en inventer une.
 */
export interface MealPlan {
  id: Id;
  /** ISO 8601, jour seul : AAAA-MM-JJ. */
  date: string;
  meal: MealSlot;
  recipe_id: Id;
  /**
   * Qui a prévu ce repas (US-48). Le plan reste **commun** — son usage est de
   * se mettre d'accord — mais deux personnes peuvent prévoir le même créneau :
   * `UNIQUE (date, meal)` est devenue `UNIQUE (date, meal, member_id)`. `null`
   * pour les lignes d'avant la migration `011`, qui appartiennent au foyer.
   */
  member_id: Id | null;
  created_at: Iso8601;
}

/**
 * Table `share_link` (US-33) : un lien public de lecture seule vers une
 * recette. `token` est un secret opaque tiré au hasard, jamais l'identifiant
 * de la recette — deviner un lien reviendrait à deviner 32 octets.
 *
 * `revoked_at` rend le partage réversible : partager n'est pas publier.
 */
export interface ShareLink {
  token: string;
  recipe_id: Id;
  created_at: Iso8601;
  /** `null` tant que le lien est actif. */
  revoked_at: Iso8601 | null;
}

/**
 * Table `shopping_item` (US-28) : une ligne de liste de courses.
 *
 * Ce n'est **pas** une ligne d'ingrédient. Elle porte la quantité déjà mise à
 * l'échelle des portions choisies à l'ajout (§3.1), elle agrège plusieurs
 * recettes, et elle survit à l'archivage de celle qui l'a produite.
 */
export interface ShoppingItem {
  id: Id;
  /** Nom tel qu'écrit dans la recette — c'est celui qu'on lit dans le magasin. */
  name: string;
  /** Minuscules, sans accent, au singulier. Clé d'agrégation (`normalizeIngredientName.ts`). */
  name_normalized: string | null;
  /** Nullable : « sel, poivre » n'a pas de quantité, et on ne la devine pas (§3.2). */
  quantity: number | null;
  unit: Unit | null;
  /** Rayon du magasin, déduit du nom puis modifiable à la main. */
  aisle: string | null;
  checked: Bool01;
  /**
   * Recette d'origine, ou `null` pour une ligne ajoutée à la main. La
   * provenance est une information utile, pas une dépendance : `ON DELETE SET
   * NULL` (005_shopping.sql).
   */
  recipe_id: Id | null;
  /**
   * À qui appartient cette ligne (US-48). `null` = **liste commune**, ce qui
   * préserve exactement le comportement d'avant la migration `011`. Une
   * recette privée n'alimente que la liste personnelle de son propriétaire.
   */
  member_id: Id | null;
  created_at: Iso8601;
}

/**
 * Table `step_ingredient`. Clé primaire composite.
 * Renseignée par appariement automatique, corrigeable à la main (US-08).
 */
export interface StepIngredient {
  step_id: Id;
  ingredient_id: Id;
}

/**
 * Rôles d'un membre (US-42, migration `009_member.sql`).
 *
 * Trois valeurs, dans une colonne. Une table `role` pour trois valeurs serait
 * l'abstraction anticipée que proscrit le §5 — et une famille de trois
 * personnes n'a pas de matrice de permissions.
 *
 * `lecture` sert d'abord à l'enfant qui grandit : consulter et cuisiner, tenir
 * son journal, sans pouvoir modifier ni archiver. Le passage à `membre` est un
 * changement de valeur, pas une migration.
 */
export const MEMBER_ROLES = ['proprietaire', 'membre', 'lecture'] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];

export function isMemberRole(value: unknown): value is MemberRole {
  return typeof value === 'string' && (MEMBER_ROLES as readonly string[]).includes(value);
}

/**
 * Visibilité d'une recette (US-43, migration `010_visibility.sql`).
 *
 * **Invisible signifie inexistante** : une recette `private` se comporte, pour
 * qui n'en est pas le propriétaire, exactement comme une recette qui n'aurait
 * jamais été créée — même code de réponse, même compteur, même état vide.
 * Toute divergence observable est un défaut.
 */
export const RECIPE_VISIBILITIES = ['private', 'shared'] as const;
export type RecipeVisibility = (typeof RECIPE_VISIBILITIES)[number];

export function isRecipeVisibility(value: unknown): value is RecipeVisibility {
  return typeof value === 'string' && (RECIPE_VISIBILITIES as readonly string[]).includes(value);
}

/**
 * Nature du plat (US-55).
 *
 * Liste **fermée**, et volontairement courte : une liste déroulante longue se
 * lit mal, et chaque valeur ajoutée est une frontière à discuter. `base` couvre
 * ce qui n'est pas un plat mais un composant — pâte feuilletée, crème
 * pâtissière, sauce —, qui représente une part réelle du corpus et n'avait
 * nulle part où aller.
 *
 * Élargir la liste plus tard demande une migration et une reprise : c'est la
 * contrepartie assumée d'une valeur validée plutôt que d'un champ libre.
 */
export const RECIPE_COURSES = ['entree', 'plat', 'dessert', 'accompagnement', 'boisson', 'base'] as const;
export type RecipeCourse = (typeof RECIPE_COURSES)[number];

export function isRecipeCourse(value: unknown): value is RecipeCourse {
  return typeof value === 'string' && (RECIPE_COURSES as readonly string[]).includes(value);
}

/** Une personne qui se connecte (US-42, complétée par US-52). */
export interface Member {
  id: Id;
  /**
   * **Ne porte plus de sens** (US-52, migration `014`), comme `recipe.status`
   * depuis `013`. La colonne est `NOT NULL` depuis `009` et une migration
   * appliquée ne se modifie pas : `members/store.ts` y recopie « prénom nom »
   * pour qu'une lecture directe de la base reste intelligible. Rien ne la lit.
   */
  name: string;
  /**
   * Prénom. C'est **le seul nom que l'application affiche** : « de Claire »,
   * « modifiée par Claire », « d'après le pot-au-feu d'Arthur ». Le nom complet
   * n'apparaît que sur le profil et dans la gestion des membres.
   *
   * Porte le libellé de l'appareil sur une session partagée (US-50), qui n'est
   * pas une ligne de cette table.
   */
  first_name: string;
  last_name: string | null;
  /**
   * **L'identifiant de connexion** (US-52), unique à la casse près. Non nul
   * pour un membre ; le seul `null` du système est la session d'appareil, qui
   * n'est pas une ligne de `member` et ne se connecte pas.
   */
  email: string | null;
  password_hash: string;
  password_salt: string;
  role: MemberRole;
  /** Incrémentée pour faire tomber toutes les sessions de ce membre, et d'aucun autre. */
  session_epoch: number;
  created_at: Iso8601;
  /** On désactive, on ne supprime jamais (§2). */
  disabled_at: Iso8601 | null;
  /**
   * Quand cette personne a vu l'écran des premiers pas (US-104, migration
   * `032`). Nulle = elle ne l'a pas vu, et il s'affichera à sa prochaine
   * ouverture — y compris pour les comptes qui existaient avant la story.
   *
   * En base et non dans `localStorage` : c'est un fait sur la personne, pas
   * sur l'appareil, et « une seule fois » veut dire une seule fois, pas une
   * fois par téléphone. La migration développe l'argument.
   */
  welcomed_at: Iso8601 | null;
}

/**
 * Statut de rangement **d'un membre** sur une recette (US-51, migration
 * `013`). Remplace `recipe.status`, qui ne porte plus de sens : ranger dépend
 * de qui regarde, décrire la recette non.
 */
export interface RecipeMemberStatus {
  recipe_id: Id;
  member_id: Id;
  status: RecipeStatus;
  updated_at: Iso8601;
}

/**
 * Étiquette posée **par un membre** sur une recette (US-51) : ses étiquettes
 * manuelles, et les deux étiquettes calculées qui dérivent de son journal
 * (« à refaire », « jamais cuisinée »).
 *
 * `recipe_tag` ne garde que ce qui décrit la recette — « moins de 30 min »,
 * « sans four » —, vrai pour tout le monde.
 */
export interface RecipeMemberTag {
  recipe_id: Id;
  tag_id: Id;
  member_id: Id;
}

/**
 * Un réglage de l'instance (US-69, migration `024`).
 *
 * **De la maison, pas d'une personne** : la clé d'API et le modèle sont réglés
 * par le `proprietaire` et valent pour tout le monde. Le vocabulaire des clés
 * est fermé et vit dans `server/src/import/anthropicConfig.ts` ; aucune route
 * ne permet d'en écrire une autre.
 *
 * `value` est nullable au sens du schéma, mais **aucune ligne n'est jamais
 * écrite à `null`** : retirer un réglage supprime sa ligne, comme
 * `recipe_member_status`. « Aucune ligne » et « valeur vide » diraient sinon la
 * même chose de deux façons.
 */
export interface InstanceSetting {
  key: string;
  value: string | null;
  updated_at: Iso8601;
}

/**
 * Une entrée du sas (US-71, migration `025`).
 *
 * **Ce qui attend, et qui n'est pas encore une recette.** « Aucun import ne
 * persiste » est devenue « aucun import n'entre dans la bibliothèque sans un
 * geste » (D2) : ce qui est capturé attend ici — visible de son seul auteur,
 * absent de la recherche, des étiquettes, des compteurs et de l'archive.
 *
 * `payload` porte un `RecipeImportPrefill` sérialisé, **et non un schéma
 * parallèle** : exactement la forme que produisent les trois imports, `origine`
 * comprise (US-70). Le type reste `string` ici parce que c'est ce que la
 * colonne contient ; la lecture typée se fait à la frontière de la route, comme
 * pour `suggestion.payload_json`.
 *
 * `expires_at` est écrite à l'insertion (trente jours) plutôt que recalculée à
 * la lecture : la péremption devient une requête, et une requête se teste.
 */
export interface ImportDraft {
  id: Id;
  member_id: Id;
  payload: string;
  /** Chemin relatif de la vignette sous `photosDir` — `sas/<id>/thumb.jpg`. `null` si l'entrée n'en porte pas. */
  image_path: string | null;
  source_url: string | null;
  created_at: Iso8601;
  expires_at: Iso8601;
}

/**
 * Le jeton du raccourci « Ajouter à Mijoté » (US-72, migration `026`).
 *
 * **Le troisième moyen de s'identifier**, après le cookie de session d'un
 * membre (US-42) et le cookie d'appareil du mode cuisine partagée (US-50).
 * Aucun des deux ne convenait : un raccourci iOS compose une requête HTTP nue,
 * sans cookies, et il écrit — ce qu'une session d'appareil ne fait jamais.
 *
 * **Sa portée tient en une ligne** : `POST /api/import/draft`, au nom de son
 * porteur. Aucune lecture. Elle est appliquée dans `auth.ts`, avant toute
 * route, et le reste répond 404.
 *
 * `token_hash` et non `token` : la forme en clair n'existe qu'une fois, dans la
 * réponse à sa création (même règle que `password_reset`, et contrairement à
 * `device_session` — voir `members/shortcutToken.ts`).
 */
export interface ShortcutToken {
  id: Id;
  member_id: Id;
  /** « iPhone d'Arthur ». Ce qu'on lit pour savoir lequel révoquer. */
  label: string;
  token_hash: string;
  created_at: Iso8601;
  /** Écrite au plus une fois par jour. `null` = jamais utilisé, et c'est la valeur qui parle. */
  last_used_at: Iso8601 | null;
  /** On révoque, on ne supprime pas (§2). */
  revoked_at: Iso8601 | null;
}

/**
 * Toutes les tables du §3, dans l'ordre de création. Utilisé par les tests et
 * l'export (US-12) — l'ordre compte : une table ne peut être restaurée
 * qu'après celles dont ses clés étrangères dépendent.
 *
 * `collection` (US-21) et `shopping_item` (US-28) sont en fin de liste : la
 * première ne référence aucune autre table, la seconde référence `recipe`,
 * déjà restaurée à ce stade. Une archive antérieure à ces stories ne les
 * contient pas et se restaure quand même — `restoreExport.ts` traite une
 * table absente comme vide.
 *
 * **`instance_setting` en est délibérément absente** (US-69) : elle porte la
 * clé d'API de l'instance, et l'archive d'export circule par courriel et par
 * clé USB. Même raison que `member`, qui porte les empreintes de mot de passe
 * et n'y figure pas non plus.
 *
 * **`import_draft` non plus** (US-71) : le sas contient ce qu'on n'a pas encore
 * décidé de garder, il est personnel, et il périme. Une sauvegarde de recettes
 * n'a rien à faire de ce qui n'est pas encore une recette.
 *
 * **`shortcut_token` non plus** (US-72) : c'est un moyen de s'identifier, au
 * même titre que `member`. Une archive qui l'emporterait ferait voyager par
 * courriel de quoi écrire dans le sas de quelqu'un — et une restauration la
 * ferait revivre après une révocation.
 *
 * L'ajout d'une table à cette liste est un geste, jamais une symétrie
 * machinale : des tests l'affirment nommément pour ces trois-là.
 */
export const TABLES = [
  'recipe',
  'ingredient',
  'step',
  'cook_log',
  'tag',
  'recipe_tag',
  'step_ingredient',
  'collection',
  'shopping_item',
  'share_link',
  'meal_plan',
  // Rangement personnel (US-51) : après `recipe` et `tag`, dont elles
  // dépendent.
  'recipe_member_status',
  'recipe_member_tag',
] as const;
export type TableName = (typeof TABLES)[number];
